import { Events, on } from './events.js';

class CartSavings extends HTMLElement {
  connectedCallback() {
    try {
      this.__lastCart = null;
      this._productCache = Object.create(null);
      // Initial render from global, else fetch
      const initial = this.#getCart();
      if (initial) {
        this.#renderFrom(initial);
      } else {
        this.#fetchAndRender();
      }
      // Listen for canonical cart updates
      this.__un = on(Events.cart.update, (ev) => {
        try {
          const cart = (ev && ev.detail && ev.detail.cart) || this.#getCart();
          this.#renderFrom(cart);
        } catch (_) {}
      });
      // Legacy/global mirror
      try {
        this.__onCartState = (ev) => { try { const cart = ev && ev.detail && ev.detail.cart; this.#renderFrom(cart || this.#getCart()); } catch (_) {} };
        document.addEventListener('cartstate:change', this.__onCartState, true);
      } catch (_) {}
    } catch (_) {}
  }

  disconnectedCallback() {
    try { this.__un && this.__un(); } catch (_) {}
    try { this.__onCartState && document.removeEventListener('cartstate:change', this.__onCartState, true); } catch (_) {}
  }

  #getCart() {
    try { return (window.store && window.store.cartState) || null; } catch (_) { return null; }
  }

  async #fetchAndRender() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      await this.#renderFrom(cart);
    } catch (_) {}
  }

  #formatMoney(cents) {
    try {
      const value = typeof cents === 'number' ? Math.round(cents) : parseInt(String(cents||0), 10) || 0;
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt = window.Shopify.money_format || undefined;
        return window.Shopify.formatMoney(value, fmt);
      }
      return (value/100).toFixed(2);
    } catch (_) {
      try { return (Number(cents||0)/100).toFixed(2); } catch (__) { return String(cents); }
    }
  }

  async #renderFrom(cart) {
    try {
      this.__lastCart = cart || null;
      await this.#primeProductData(cart && cart.items);
      const original = this.#computeOriginalSubtotal(cart);
      const final = cart && Number.isFinite(Number(cart.total_price)) ? Number(cart.total_price) : 0;
      const saved = Math.max(0, original - final);
      const elOriginal = this.querySelector('[data-role="original"]');
      const elFinal = this.querySelector('[data-role="final"]');
      const elSaved = this.querySelector('[data-role="saved"]');
      if (elOriginal) elOriginal.innerHTML = this.#formatMoneyHtml(original);
      if (elFinal) elFinal.innerHTML = this.#formatMoneyHtml(final);
      if (elSaved) elSaved.innerHTML = this.#formatMoneyHtml(saved);
      // Toggle visibility if no savings
      const hasSavings = saved > 0;
      this.toggleAttribute('data-has-savings', hasSavings);
      if (elOriginal) elOriginal.toggleAttribute('hidden', !hasSavings);
      if (elSaved) elSaved.toggleAttribute('hidden', !hasSavings);
    } catch (_) {}
  }

  #computeOriginalSubtotal(cart) {
    try {
      if (!cart || !Array.isArray(cart.items)) return Number(cart && cart.total_price) || 0;
      let sum = 0;
      (cart.items || []).forEach((it) => {
        try {
          const finalLine = Number((it && (it.final_line_price != null ? it.final_line_price : it.line_price)) || 0);
          const fallbackOrig = Number((it && it.original_line_price != null ? it.original_line_price : finalLine) || 0);
          let origLine = finalLine + this.#computeLineSaved(it);
          if (origLine < fallbackOrig) origLine = fallbackOrig;
          if (origLine < finalLine) origLine = finalLine;
          sum += origLine;
        } catch (_) {}
      });
      return sum;
    } catch (_) { return Number(cart && cart.total_price) || 0; }
  }

  #resolveCompareAt(item) {
    try {
      const sources = [];
      if (item && item.compare_at_price != null) sources.push(item.compare_at_price);
      if (item && item.variant_compare_at_price != null) sources.push(item.variant_compare_at_price);
      if (item && item.variant && item.variant.compare_at_price != null) sources.push(item.variant.compare_at_price);
      if (item && item.product && item.product.compare_at_price != null) sources.push(item.product.compare_at_price);
      if (item && item.product && item.product.compare_at_price_max != null) sources.push(item.product.compare_at_price_max);
      const handle = item && item.handle;
      if (handle) {
        let productData = null;
        if (this._productCache && this._productCache[handle]) productData = this._productCache[handle];
        if (productData && productData !== false) {
          try {
            if (productData.compare_at_price_max != null) sources.push(productData.compare_at_price_max);
            if (productData.compare_at_price != null) sources.push(productData.compare_at_price);
            const variants = productData.variants || [];
            const vid = Number(item && (item.variant_id != null ? item.variant_id : item.id));
            variants.forEach((variant) => {
              try {
                if (!variant) return;
                if (vid && Number(variant.id) !== vid) return;
                if (variant.compare_at_price != null) sources.push(variant.compare_at_price);
              } catch (_) {}
            });
          } catch (_) {}
        }
      }
      for (let i = 0; i < sources.length; i += 1) {
        const raw = sources[i];
        const value = Number(raw);
        if (Number.isFinite(value) && value > 0) return Math.round(value);
      }
      return 0;
    } catch (_) { return 0; }
  }

  #computeLineSaved(item) {
    try {
      if (!item) return 0;
      const qty = Number(item && item.quantity) || 0;
      const unitFinal = (item && (item.final_price != null ? Number(item.final_price) : Number(item.price))) || 0;
      let unitOrig = (item && item.original_price != null ? Number(item.original_price) : unitFinal) || 0;
      const plan = item && item.selling_plan_allocation;
      const planCompare = plan && plan.compare_at_price != null ? Number(plan.compare_at_price) : 0;
      if (planCompare > 0) {
        if (planCompare > unitOrig) unitOrig = planCompare;
      } else {
        const cmp = this.#resolveCompareAt(item);
        if (cmp > unitOrig) unitOrig = cmp;
      }
      let diff = (unitOrig - unitFinal) * (qty || 1);
      if (diff <= 0) {
        const finalLine = Number((item && (item.final_line_price != null ? item.final_line_price : item.line_price)) || 0);
        const origLine = Number((item && item.original_line_price != null ? item.original_line_price : finalLine) || 0);
        diff = origLine - finalLine;
      }
      return diff > 0 ? diff : 0;
    } catch (_) { return 0; }
  }

  #formatMoneyHtml(cents) {
    try {
      const value = this.#formatMoney(cents);
      return '<span class="money">' + value + '</span>';
    } catch (_) {
      try { return '<span class="money">' + (Number(cents || 0) / 100).toFixed(2) + '</span>'; } catch (__) { return '<span class="money">' + String(cents) + '</span>'; }
    }
  }

  async #primeProductData(items) {
    try {
      if (!Array.isArray(items) || !items.length) return;
      if (!this._productCache) this._productCache = Object.create(null);
      const handles = new Set();
      items.forEach((it) => {
        try {
          const handle = it && it.handle;
          if (!handle) return;
          if (this._productCache[handle] === undefined) handles.add(handle);
        } catch (_) {}
      });
      if (!handles.size) return;
      await Promise.all(Array.from(handles).map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
          const data = await res.json();
          this._productCache[handle] = data;
        } catch (_) {
          this._productCache[handle] = false;
        }
      }));
    } catch (_) {}
  }
}

try { if (!customElements.get('cart-savings')) customElements.define('cart-savings', CartSavings); } catch (_) {}


